import React, { useState, useCallback, useEffect, useRef, useContext, useMemo } from 'react'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { useField, useFormikContext } from 'formik'
import IconButton from 'components/iconButton'
import CheckIcon from '@material-ui/icons/Check'
import ClearIcon from '@material-ui/icons/Clear'
import MuiCreateIcon from '@material-ui/icons/Create'
import clsx from 'clsx'
import { useAbility } from 'currentContext'
import EntityContext from 'components/item/context'
import useKeyPress from 'hooks/useKeyPress'
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'
import has from 'lodash/has'
import { getInitialValues } from 'utils'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import { useRouteMatch } from 'react-router-dom'
import { subject } from '@casl/ability'
import { AuthorisableEntityContext } from 'components/item/item'
import { isNil } from 'lodash'
import isEmpty from 'lodash/isEmpty'

const useStyles = makeStyles((theme) => ({
  componentWrapper: {
    placeContent: 'center space-between',
    borderRadius: theme.shape.borderRadius,
    alignItems: 'center',
    position: 'relative',
    width: '50%',
    height: 'auto',
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  displayFlex: {
    display: 'flex'
  },
  wrapperPadding: {
    padding: '6.5px 14px'
  },
  buttonContainer: {
    zIndex: 10,
    '& button': {
      boxShadow: 'rgb(9 30 66 / 25%) 0px 4px 8px -2px, rgb(9 30 66 / 31%) 0px 0px 1px'
    },
    display: 'flex',
    flexShrink: 0
  },
  buttonContainerBottom: {
    paddingTop: theme.spacing(0.75)
  },
  buttonContainerRight: {
    paddingLeft: theme.spacing(1)
  },
  buttonSpacing: {
    marginLeft: theme.spacing(0.75)
  },
  noTopPadding: {
    paddingTop: 0
  },
  hoverStyle: {
    '& $editButton': {
      display: 'none'
    },
    '& $fakeEditButton': {
      display: 'block'
    },
    '&:hover': {
      '& $editButton': {
        display: 'block'
      },
      '& $fakeEditButton': {
        display: 'none'
      },
      borderColor: theme.palette.inputs.hover[100],
      background: theme.palette.inputs.hover[100]
    }
  },
  value: {
    display: 'flex',
    flexGrow: 1,
    fontSize: '0.929rem',
    flexWrap: 'wrap',
    whiteSpace: 'pre-wrap'
  },
  valueActions: {
    justifyContent: 'flex-end',
    fontSize: '1.25rem',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  editButton: {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0, 0.5),
    position: 'absolute',
    right: '5px'
  },
  fakeEditButton: {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0, 0.5),
    position: 'absolute',
    right: '5px'
  },
  withError: {
    border: '1px solid red'
  },
  adjustRight: {
    justifyContent: 'flex-end',
    paddingRight: 20
  },
  valueShadow: {
    borderColor: theme.palette.inputs.hover[100],
    background: theme.palette.inputs.hover[100]
  },
  shadowWidth: {
    width: '100%'
  },
  inputWrapper: {
    display: 'flex',
    '& .MuiAutocomplete-root': {
      flexGrow: 1
    }
  },
  editButtonWrapper: {
    minWidth: 80,
    width: 80,
    display: 'flex',
    alignItems: 'flex-start',
    paddingLeft: theme.spacing(1),
    '& .MuiButtonBase-root': {
      padding: 3
    }
  },
  fullWidth: {
    width: '100%'
  }
}))

export const CreateIcon = withStyles(() => ({
  root: {
    fontSize: '1.25rem'
  }
}))(MuiCreateIcon)

export const TransparentCreateIcon = withStyles(() => ({
  root: {
    fontSize: '1.25rem',
    color: 'transparent'
  }
}))(MuiCreateIcon)

function transformSelectProps(props: object, isDisabled: boolean, submitForm: () => Promise<void>) {
  let componentProps = cloneDeep(props)
  const hasSelectProps = has(componentProps, 'SelectProps')

  if (hasSelectProps) {
    const { SelectProps, ...otherProps } = componentProps
    const { AutocompleteProps, TextFieldProps, ...otherSelectProps } = SelectProps

    componentProps = {
      ...otherProps,
      SelectProps: {
        ...otherSelectProps,
        AutocompleteProps: {
          ...AutocompleteProps,
          open: true,
          onChange: submitForm,
          disabled: isDisabled
        },
        TextFieldProps: {
          ...TextFieldProps,
          disabled: isDisabled,
          autoFocus: true
        }
      }
    }
  } else {
    const { AutocompleteProps, TextFieldProps, ...otherProps } = componentProps

    componentProps = {
      ...otherProps,
      AutocompleteProps: {
        ...AutocompleteProps,
        open: true,
        onChange: submitForm,
        disabled: isDisabled
      },
      TextFieldProps: {
        ...TextFieldProps,
        disabled: isDisabled,
        autoFocus: true
      }
    }
  }

  return componentProps
}

export const optionsSchema = {
  editable: {
    type: 'boolean',
    defaultValue: true
  },
  isSelect: {
    type: 'boolean',
    defaultValue: false
  },
  autoFocus: {
    type: 'boolean',
    defaultValue: true
  },
  showShadow: {
    type: 'boolean',
    defaultValue: false
  },
  disableControlsPortal: {
    type: 'boolean',
    defaultValue: true
  },
  prevValueRefetch: {
    type: 'boolean',
    defaultValue: false
  },
  disabledEnter: {
    type: 'boolean',
    defaultValue: false
  },
  noFormReset: {
    type: 'boolean',
    defaultValue: false
  }
} as const

interface InlineEditFieldOptionsType {
  editable?: boolean
  isSelect?: boolean
  autoFocus?: boolean
  showShadow?: boolean
  disableControlsPortal?: boolean
  prevValueRefetch?: boolean
  disabledEnter?: boolean
  fullWidth?: boolean
  noFormReset?: boolean
}

export function withInlineWrapper<A extends object, V = unknown, P extends { [x: string]: any } = {}>(
  Component: React.ComponentType<P>,
  getTextValue: (value: V, props: P & A, pickersContext?: any, subject?: string) => React.ReactNode,
  options: InlineEditFieldOptionsType = {}
): React.ComponentType<P & A> {
  // eslint-disable-next-line react/display-name
  return (props: React.PropsWithChildren<P>) => {
    const classes = useStyles()
    const elementRef = useRef()
    const wrapperRef = useRef()
    const ability = useAbility()
    const context = useContext(EntityContext)
    const authContext = useContext(AuthorisableEntityContext)
    const enterPressed = useKeyPress('Enter')
    const escPressed = useKeyPress('Escape')
    const multipleFields = Array.isArray(props.name)
    const {
      params: { id: subjectId }
    } = useRouteMatch<{ id: string }>()
    const { editable, isSelect, autoFocus, showShadow, prevValueRefetch, noFormReset } = getInitialValues(options, optionsSchema)
    const { submitForm, isSubmitting, errors, initialValues, isValid, validateForm, resetForm } = useFormikContext()

    const updateAbility = (fieldName: string): boolean => {
      if (isNil(authContext)) {
        return ability.can('update', subject(context.authorizationSubject, { id: subjectId }), fieldName)
      }
      return ability.can('update', authContext.entity, fieldName)
    }

    const readOnly = useMemo(() => {
      if (!isValid) {
        // if parent object is in invalid state, prevent it from becoming editable
        // this is a workaround while proper fix is devised for silently failing updates
        return true
      }

      if (!editable || (props?.TextFieldProps?.disabled as boolean)) {
        return true
      }

      if (multipleFields) {
        return props.name.find((fieldName: string): boolean => !updateAbility(fieldName)) !== undefined
      } else {
        return !updateAbility(props.name)
      }
    }, [editable, multipleFields, context, ability, subjectId, isValid])

    let value: V | V[]
    let prevValue: Array<React.MutableRefObject<V | V[]>> | React.MutableRefObject<V | V[]>

    if (props.name === undefined) {
      value = undefined as unknown as V

      prevValue = useRef(value)
    } else if (multipleFields) {
      value = []
      prevValue = []

      props.name.forEach((name) => {
        ;(value as V[]).push(useField(name)[0].value)
        prevValue.push(useRef(useField(name)[0].value))
      })
    } else {
      ;[{ value }] = useField(props.name)
      prevValue = useRef(value)
    }

    let componentProps = {
      ...props
    }
    const refProps = isSelect || !autoFocus || multipleFields ? {} : { ref: elementRef }

    const [editing, setEditing] = useState(false)

    const enableEditMode = useCallback(() => {
      setEditing(true)
    }, [])

    const handleCancel = useCallback(() => {
      if (!noFormReset) {
        resetForm()
      }

      setEditing(false)
    }, [noFormReset, resetForm])

    const handleSubmit = useCallback(async () => {
      let validationErrors = {}

      if (multipleFields) {
        let needsSubmit = false

        ;(value as V[]).forEach((name, index) => {
          if (name !== prevValue[index].current) {
            needsSubmit = true

            prevValue[index].current = name
          }
        })
        /* eslint-disable no-void */
        if (needsSubmit) {
          await submitForm()
        }
        /* eslint-enable no-void */
      } else {
        if (value !== prevValue.current || isSelect) {
          /* eslint-disable no-void */
          await submitForm()
          /* eslint-enable no-void */
          if (!prevValueRefetch) {
            prevValue.current = value
          }
        }
      }
      validationErrors = await validateForm()

      if (isEmpty(validationErrors)) {
        setEditing(false)
      }
    }, [value, prevValue])

    const fieldError = useMemo(() => get(errors, props.name), [errors])

    if (isSelect) {
      const isDisabled = isSubmitting || readOnly

      componentProps = transformSelectProps(componentProps, isDisabled, handleSubmit)
    }

    useEffect(() => {
      if (editing && autoFocus) {
        elementRef?.current?.focus()
      }
    }, [editing])

    useEffect(() => {
      if (editing && !isSubmitting && !isSelect) {
        if (enterPressed && !options?.disabledEnter && !props.multiline) {
          /* eslint-disable no-void */
          void handleSubmit()
          /* eslint-enable no-void */
        }
        if (escPressed) {
          handleCancel()
        }
      }
    }, [enterPressed, escPressed])

    useEffect(() => {
      if (!isSubmitting && !multipleFields && prevValueRefetch) {
        prevValue.current = initialValues[props.name]
      }
    }, [isSubmitting, initialValues])

    return (
      <div
        data-input-name={props.name}
        onClick={
          editing || readOnly
            ? () => null
            : () => {
                const hightlight = getSelection()?.toString()
                if (!hightlight) {
                  enableEditMode()
                }
              }
        }
        className={clsx(classes.componentWrapper, {
          [classes.hoverStyle]: !editing,
          [classes.withError]: !editing && fieldError !== undefined,
          [classes.wrapperPadding]: !editing,
          [classes.displayFlex]: !editing,
          [classes.valueShadow]: showShadow && !editing,
          [classes.shadowWidth]: showShadow,
          [classes.fullWidth]: options?.fullWidth
        })}
        ref={wrapperRef}
      >
        {editing ? (
          <ClickAwayListener onClickAway={handleCancel}>
            <div className={classes.inputWrapper}>
              <Component {...componentProps} {...refProps} disabled={isSubmitting} />
              {!isSelect && (
                <div className={classes.editButtonWrapper}>
                  <IconButton aria-label='Save' onClick={handleSubmit} color='light' disabled={isSubmitting}>
                    <CheckIcon />
                  </IconButton>
                  <IconButton aria-label='Cancel' onClick={handleCancel} color='light' disabled={isSubmitting} className={classes.buttonSpacing}>
                    <ClearIcon />
                  </IconButton>
                </div>
              )}
            </div>
          </ClickAwayListener>
        ) : (
          <>
            <div className={clsx(classes.value, { [classes.adjustRight]: props.adjustRight === true })}>{getTextValue(value, props)}</div>
            {!readOnly && (
              <>
                <IconButton aria-label='edit' className={classes.editButton} onClick={enableEditMode} color='transparent'>
                  <CreateIcon />
                </IconButton>
                <IconButton className={classes.fakeEditButton} color='transparent'>
                  <TransparentCreateIcon />
                </IconButton>
              </>
            )}
          </>
        )}
      </div>
    )
  }
}

export default withInlineWrapper
